import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import CheckCircle from '@/assets/icons/check-circle.svg';
import ExclamationCircle from '@/assets/icons/exclamation-circle.svg';
import PencilEdit from '@/assets/icons/pencil-edit.svg';
import PlusSm from '@/assets/icons/plus-sm.svg';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { cn } from '@/utils/classnames';
import { formatShortDate } from '@/utils/date-util';

/**
 * Pure helper that decides whether to show the banner and returns
 * all data needed to render it.
 *
 * Inputs:
 * - daysLeft: number | null
 * - actualHires: number | null
 * - expectedHires: number | null
 * - endDate: string (already formatted)
 * - intl: react-intl's intl object for formatting
 *
 * Returns:
 * - { show: boolean, icon?: string, title?: string, description?: string, isSuccess?: boolean }
 */
type HiringBannerData =
  | { show: false }
  | {
      show: true;
      icon: string;
      title: string;
      description: string;
      isSuccess: boolean;
    };

function getHiringBannerData({
  daysLeft,
  actualHires,
  expectedHires,
  endDate,
  intl,
}: {
  daysLeft: number | null;
  actualHires: number | null;
  expectedHires: number | null;
  endDate: string;
  intl: IntlShape;
}): HiringBannerData {
  const hasHiresInfo = actualHires != null && expectedHires != null;
  const goalEnded = daysLeft === 0;
  const meetsTarget = hasHiresInfo && actualHires >= expectedHires;
  const missesTarget = hasHiresInfo && actualHires < expectedHires;
  const endsTomorrow = daysLeft === 1;

  if (goalEnded && missesTarget) {
    return {
      show: true,
      icon: ExclamationCircle,
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ended without reaching target',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate} with {actualHires} out of {expectedHires} hires. Kindly create a new goal if your requirement is still open.',
        },
        { endDate, actualHires, expectedHires },
      ),
      isSuccess: false,
    };
  }

  if (goalEnded && meetsTarget) {
    return {
      show: true,
      icon: CheckCircle,
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal completed and target met!.',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate}. Create a new goal to continue tracking sourcing spend.',
        },
        { endDate },
      ),
      isSuccess: true,
    };
  }

  if (endsTomorrow) {
    return {
      show: true,
      icon: ExclamationCircle,
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ends in 1 day',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal will end on {endDate}. Kindly consider extending the end date if the requirement is still open.',
        },
        { endDate },
      ),
      isSuccess: false,
    };
  }

  return { show: false };
}

function PerformanceStatusBanner() {
  const intl = useIntl();
  const { hiringGoal, hiringGoalUrl } = useOpeningContext();

  if (!hiringGoal) return null;

  // Basic values
  const expectedDuration = hiringGoal.duration;
  const elapsedDays = hiringGoal.days_elapsed;
  const daysLeft =
    expectedDuration != null && elapsedDays != null
      ? expectedDuration - elapsedDays
      : null;

  const endDate = formatShortDate(hiringGoal.hire_by);
  const actualHires = hiringGoal.hire_stats?.actual;
  const expectedHires = hiringGoal.hire_stats?.expected;
  const endsTomorrow = daysLeft === 1;

  const bannerData = getHiringBannerData({
    daysLeft,
    actualHires,
    expectedHires,
    endDate,
    intl,
  });

  if (!bannerData.show) return null;

  const {
    icon: iconSrc = CheckCircle,
    title,
    description,
    isSuccess = false,
  } = bannerData;

  return (
    <div
      className={cn(
        'border-1 flex items-center gap-2 self-stretch rounded-md p-2',
        isSuccess
          ? 'bg-fx-green-50 border-fx-green'
          : 'bg-fx-amber-50 border-fx-amber',
      )}
    >
      <img src={iconSrc} alt="" />
      <div className="flex grow flex-col items-start justify-center">
        <Typography variant="body2">{title}</Typography>
        <Typography variant="body1">{description}</Typography>
      </div>
      {hiringGoalUrl && (
        <Button variant="secondary" asChild>
          <Link to={String(hiringGoalUrl)} target="_blank">
            <img
              src={endsTomorrow ? PencilEdit : PlusSm}
              alt={endsTomorrow ? 'Edit' : 'Add'}
            />
            {endsTomorrow ? (
              <FormattedMessage defaultMessage="Edit Goal" />
            ) : (
              <FormattedMessage defaultMessage="New Goal" />
            )}
          </Link>
        </Button>
      )}
    </div>
  );
}

export { PerformanceStatusBanner };
